import { Box, Dialog, InputBase, Typography, useTheme } from '@mui/material';
import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlatNavItems } from './navConfig';

/**
 * Command palette — DESIGN-SYSTEM.md §5.10 / §3.2.
 *
 * v1 scope: static navigation only (the flat, permission-filtered list the
 * sidebar/breadcrumb already share via `navConfig.js`). No live entity
 * search (customers/products/orders) yet — that would need its own
 * data-fetching design and is left for a follow-up, per the open question
 * raised in DESIGN-SYSTEM.md §11.
 */
export default function CommandPalette({ open, onClose }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const navItems = useFlatNavItems();
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (open) setQuery('');
    }, [open]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return navItems;
        return navItems.filter((item) =>
            item.label.toLowerCase().includes(q) || item.sectionLabel.toLowerCase().includes(q)
        );
    }, [navItems, query]);

    const handleSelect = (path) => {
        navigate(path);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    // Só ≥ sm: abaixo disso o tema ancora o modal no rodapé (sheet).
                    position: { xs: 'relative', sm: 'fixed' },
                    top: { xs: 'auto', sm: '12vh' },
                    m: 0,
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Search size={18} color={theme.palette.text.disabled} />
                <InputBase
                    autoFocus
                    fullWidth
                    placeholder="Digite um comando ou busque…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{ fontSize: 15 }}
                />
                <Box component="kbd" sx={{ fontSize: 11, border: `1px solid ${theme.palette.divider}`, borderRadius: '6px', px: '6px', py: '2px', color: 'text.disabled' }}>
                    Esc
                </Box>
            </Box>
            <Box sx={{ maxHeight: 340, overflowY: 'auto', p: 1 }}>
                {results.length === 0 && (
                    <Typography sx={{ p: 2, fontSize: 13, color: 'text.secondary' }}>
                        Nenhum resultado para “{query}”.
                    </Typography>
                )}
                {results.length > 0 && (
                    <>
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', px: 1.25, py: '6px' }}>
                            Navegar
                        </Typography>
                        {results.map((item) => {
                            const Icon = item.icon;
                            return (
                                <Box
                                    key={item.id}
                                    onClick={() => handleSelect(item.path)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        px: 1.25,
                                        py: 1.1,
                                        borderRadius: '10px',
                                        fontSize: '13.5px',
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: 'background.subtle' },
                                    }}
                                >
                                    <Icon size={16} color={theme.palette.text.disabled} />
                                    <span>{item.label}</span>
                                    <Typography sx={{ ml: 'auto', fontSize: '10.5px', color: 'text.disabled' }}>
                                        {item.sectionLabel}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </>
                )}
            </Box>
        </Dialog>
    );
}
